import { useMemo, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../helpers/apiRequester'
import { useIsSentinelUser } from '../../authUserProvider'
import styles from './styles.module.scss'

// Reports selectable from the dropdown. `value` is the backend reportType,
// `filename` the download slug. All are admin/Sentinel-only (gated server-side
// for any reportType starting "mfc-", and this whole component is hidden from
// non-Sentinel users below).
const REPORT_OPTIONS = [
    { value: 'mfc-fundable-clients', label: 'Clients with Claims (Fundable)', filename: 'fundable-clients' },
    { value: 'mfc-signed-clients', label: 'All Clients Eligibility', filename: 'all-clients-eligibility' },
    { value: 'mfc-claims-eligibility', label: 'Claims Eligibility Breakdown', filename: 'claims-eligibility' },
    { value: 'mfc-signups-all', label: 'Signed to Date', filename: 'signed-to-date' },
    { value: 'mfc-signups-today', label: 'Signed Today', filename: 'signed-today' }
] as const

interface WeekOption {
    key: string
    label: string
    fromDate: string
    toDate: string
    inProgress: boolean
}

const LONDON_TZ = 'Europe/London'
const WEEKS_BACK = 12
const WEEK_MS = 7 * 24 * 60 * 60 * 1000
// Don't surface weeks that start before this date — there's no meaningful data
// to export before it. Compared in the same wall-clock-as-UTC space the windows
// are built in (Date.UTC, not local time).
const EARLIEST_WEEK_START_MS = Date.UTC(2026, 5, 1, 10, 0, 0) // 1 Jun 2026, 10:00

// Hard floor for the weekly dropdown — no week starting before this Monday 10am
// (London wall-clock) is shown, regardless of WEEKS_BACK. 2026-06-15 is a Monday,
// so the cap lands exactly on a week boundary.
const EARLIEST_WEEK_START = '2026-06-15 10:00:00'

// Return a Date whose UTC fields equal the Europe/London wall-clock of `date`.
// This lets us do day-of-week / hour arithmetic in "London space" (handling
// BST/GMT) without pulling in a timezone library, regardless of the browser's
// own timezone.
const londonWallClock = (date: Date): Date => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: LONDON_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    })
        .formatToParts(date)
        .reduce<Record<string, string>>((acc, part) => {
            acc[part.type] = part.value
            return acc
        }, {})

    const hour = parts.hour === '24' ? 0 : Number(parts.hour)
    return new Date(
        Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), hour, Number(parts.minute), Number(parts.second))
    )
}

// Format a London-wall-clock Date as a naive 'YYYY-MM-DD HH:mm:ss' string for
// the SQL range. The Date's UTC fields already hold London wall-clock, so the
// ISO string (sliced) is the literal London time.
const toSqlString = (wallClock: Date): string => wallClock.toISOString().slice(0, 19).replace('T', ' ')

const toDayLabel = (wallClock: Date): string =>
    new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(wallClock)

// Build the selectable list of Monday-10am -> Monday-10am windows, anchored to
// Europe/London. Index 0 is the current in-progress week; the rest are completed
// weeks, most recent first.
const buildWeekOptions = (now: Date): WeekOption[] => {
    const wall = londonWallClock(now)

    const currentStart = new Date(wall)
    const daysSinceMonday = (currentStart.getUTCDay() + 6) % 7 // Mon=0 ... Sun=6
    currentStart.setUTCDate(currentStart.getUTCDate() - daysSinceMonday)
    currentStart.setUTCHours(10, 0, 0, 0)
    // If "now" is before this week's Monday 10am (e.g. Monday 09:30), the active
    // window actually started the previous Monday.
    if (wall.getTime() < currentStart.getTime()) {
        currentStart.setUTCDate(currentStart.getUTCDate() - 7)
    }

    const options: WeekOption[] = []

    const currentEnd = new Date(currentStart.getTime() + WEEK_MS)
    if (currentStart.getTime() >= EARLIEST_WEEK_START_MS) {
        options.push({
            key: toSqlString(currentStart),
            label: `This week (in progress) · from ${toDayLabel(currentStart)} 10am`,
            fromDate: toSqlString(currentStart),
            toDate: toSqlString(currentEnd),
            inProgress: true
        })
    }

    for (let i = 1; i <= WEEKS_BACK; i++) {
        const start = new Date(currentStart.getTime() - i * WEEK_MS)
        if (start.getTime() < EARLIEST_WEEK_START_MS) break
        const end = new Date(start.getTime() + WEEK_MS)
        options.push({
            key: toSqlString(start),
            label: `${toDayLabel(start)} 10am → ${toDayLabel(end)} 10am`,
            fromDate: toSqlString(start),
            toDate: toSqlString(end),
            inProgress: false
        })
    }

    // Drop any week starting before the hard floor. fromDate and the cap share
    // the 'YYYY-MM-DD HH:mm:ss' format, so a lexicographic compare is correct.
    return options.filter((option) => option.fromDate >= EARLIEST_WEEK_START)
}

const MfcReports = () => {
    const isSentinelUser = useIsSentinelUser()

    const [selectedReport, setSelectedReport] = useState<string>(REPORT_OPTIONS[0].value)
    const [reportLoading, setReportLoading] = useState(false)
    const [weekLoading, setWeekLoading] = useState(false)

    const weekOptions = useMemo(() => buildWeekOptions(new Date()), [])
    // Default to the most recent completed week ("download most recent").
    const [selectedWeekKey, setSelectedWeekKey] = useState<string>(() => {
        const firstCompleted = weekOptions.find((week) => !week.inProgress)
        return (firstCompleted || weekOptions[0])?.key ?? ''
    })

    const anyLoading = reportLoading || weekLoading

    // Fetch the generated CSV and trigger a blob download via an anchor element
    // rather than window.open. iOS Safari blocks window.open() that runs after an
    // await (it's no longer a direct user gesture), which silently fails the
    // download even though the request succeeded. A same-origin blob URL with a
    // download attribute works reliably across desktop and mobile.
    const triggerBlobDownload = async (fileUrl: string, filename: string) => {
        const csvText = await fetch(fileUrl).then((r) => r.text())
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
    }

    const downloadSelectedReport = async () => {
        const option = REPORT_OPTIONS.find((o) => o.value === selectedReport)
        if (!option) return

        setReportLoading(true)
        Loading.standard('Generating report...')

        try {
            const response = await api({ url: '/download-report', data: { reportType: option.value, isFullReport: true } })
            const fileUrl = response.data?.fileUrl
            if (!fileUrl) {
                Report.failure('Error', 'No file URL received from server', 'OK')
                return
            }

            const filename = `evans-hughes-${option.filename}-${new Date().toISOString().slice(0, 10)}.csv`
            await triggerBlobDownload(fileUrl, filename)

            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading MFC report:', error)
            Report.failure('Error', error?.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setReportLoading(false)
            Loading.remove()
        }
    }

    const downloadWeek = async () => {
        const week = weekOptions.find((option) => option.key === selectedWeekKey)
        if (!week) return

        setWeekLoading(true)
        Loading.standard('Generating report...')

        try {
            const response = await api({
                url: '/download-report',
                data: { reportType: 'mfc-clients-week', isFullReport: true, fromDate: week.fromDate, toDate: week.toDate }
            })
            const fileUrl = response.data?.fileUrl
            if (!fileUrl) {
                Report.failure('Error', 'No file URL received from server', 'OK')
                return
            }

            const filename = `evans-hughes-claims-week-${week.fromDate.slice(0, 10)}.csv`
            await triggerBlobDownload(fileUrl, filename)

            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading MFC weekly claims report:', error)
            Report.failure('Error', error?.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setWeekLoading(false)
            Loading.remove()
        }
    }

    // Admin-only feature: hide the entire report section from non-Sentinel users.
    // The backend independently enforces this for any "mfc-" reportType.
    if (!isSentinelUser) return null

    return (
        <section className={styles.reportSection}>
            <h2 className={styles.sectionTitle}>Download a Report</h2>
            <div className={styles.weekRow}>
                <select
                    className={styles.weekSelect}
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                    disabled={anyLoading}
                >
                    {REPORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <button className={styles.downloadButton} onClick={downloadSelectedReport} disabled={anyLoading}>
                    {reportLoading ? 'Generating...' : 'Download'}
                </button>
            </div>

            <h3 className={styles.subTitle}>Weekly Fundable Clients</h3>
            <p className={styles.helpText}>
                Fundable clients (same as the Fundable Clients stat) who signed their CFA in a Monday 10am → Monday 10am
                week.
            </p>
            <div className={styles.weekRow}>
                <select
                    className={styles.weekSelect}
                    value={selectedWeekKey}
                    onChange={(e) => setSelectedWeekKey(e.target.value)}
                    disabled={anyLoading}
                >
                    {weekOptions.map((week) => (
                        <option key={week.key} value={week.key}>
                            {week.label}
                        </option>
                    ))}
                </select>
                <button className={styles.downloadButton} onClick={downloadWeek} disabled={anyLoading}>
                    {weekLoading ? 'Generating...' : 'Download Week'}
                </button>
            </div>
        </section>
    )
}

export default MfcReports
